import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';
import { Location, NgIf } from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {AccountService} from '../account.service';
import {BaseComponent} from '../../core/base.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {UserProfile} from '../../api/models/user-profile';
import {EditUserProfile} from '../../api/models/edit-user-profile';
import {matchFields} from '../../core/validators/match-fields.validator';
import {NotificationService} from '../../notification/notification.service';
import {passwordValidator} from '../../core/validators/password.validator';
import {AuthService} from '../../core/security/auth.service';
import {phoneNumberValidator} from '../../core/validators/phone-number.validator';
import {matchBy} from '../../core/validators/match-by.validator';
import {LookupModel} from '../../api/models/lookup-model';
import {MatConfirmDialogComponent} from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {TwoFactorSetupDialogComponent} from '../../dialogs/two-factor-setup-dialog/two-factor-setup-dialog.component';
import { MatCard, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatPhoneNumberInputComponent } from '../../editors/mat-phone-number-input/mat-phone-number-input.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatFormFieldFix } from '../../editors/mat-form-field/mat-form-field-fix';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { FlexModule } from '@angular/flex-layout';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/core';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatPasswordInputComponent } from '../../editors/mat-password-input/mat-password-input.component';
import { MessagesModule } from 'primeng/messages';
import { PrimeTemplate } from 'primeng/api';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';
import { Message } from 'primeng/message';

@Component({
  selector: 'app-profile-edit',
  templateUrl: './profile-edit.component.html',
  styleUrls: ['./profile-edit.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCard,
    MatCardTitle,
    MatCardContent,
    NgIf,
    MatTextInputComponent,
    MatPhoneNumberInputComponent,
    MatCardActions,
    MatButton,
    MatTooltip,
    Angular2PromiseButtonModule,
    MatFormFieldFix,
    MatFormField,
    MatLabel,
    MatSelect,
    MatSelectTrigger,
    FlexModule,
    MatIcon,
    MatOption,
    MatCheckboxInputComponent,
    MatPasswordInputComponent,
    MessagesModule,
    PhoneNumberPipe,
    Message,
  ],
})
export class ProfileEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  public form: UntypedFormGroup;
  public submitted: boolean;
  public serverError: boolean;
  public profileModel: UserProfile = {
    username: '',
    email: '',
    settings: {
      useManualReportsFiltering: false,
    },
  };
  buttonSubmitPromise: Promise<void>;
  colorScheme: string;

  twoFactorProviders: LookupModel[] = [
    { text: 'Authenticator App', id: 'authenticator' },
    { text: 'Phone Number', id: 'phoneNumber' },
    { text: 'Email', id: 'email' },
  ];

  constructor(
    route: ActivatedRoute,
    titleService: Title,
    headerService: HeaderService,
    authService: AuthService,
    private accountService: AccountService,
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    private location: Location,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.headerService.showBackButton(true);

    this.createForm();
  }

  async ngOnInit() {
    super.ngOnInit();

    this.profileModel = await this.accountService.getUserProfile();

    this.ngOnChanges();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.showBackButton(false);
  }

  ngOnChanges() {
    const user = this.authService.currentUser.getValue();

    this.colorScheme = user?.uiSettings.themeClass;

    this.form.reset({
      firstName: this.profileModel.firstName,
      lastName: this.profileModel.lastName,
      phoneNumber: this.profileModel.phoneNumber,
      email: this.profileModel.email,

      useManualReportsFiltering: !!this.profileModel.settings.useManualReportsFiltering,
      emailNotificationEnabled: !!this.profileModel.settings.emailNotificationEnabled,

      oldPassword: '',
      newPassword: '',
      confirmPassword: '',

      twoFactorEnabled: this.profileModel.twoFactorEnabled,
      twoFactorProvider: this.profileModel.twoFactorProvider ?? 'authenticator',
      twoFactorSourceNumber: this.profileModel.twoFactorSource,
      twoFactorVerifyToken: '',
    });
  }

  async onSubmit() {
    this.serverError = false;

    if (this.form.valid) {
      this.submitted = true;
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<void> {
    try {
      const reqData = this.prepareFormModel();
      await this.accountService.saveUserProfile(reqData);
      this.notificationService.notifySuccess('Profile updated successfully');
      await this.authService.alive(true);
      this.goBack();
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): EditUserProfile {
    const formModel = this.form.value;

    const model: EditUserProfile = {
      firstName: formModel.firstName,
      lastName: formModel.lastName,
      phoneNumber: formModel.phoneNumber,
      email: formModel.email,
      useManualReportsFiltering: formModel.useManualReportsFiltering,
      emailNotificationEnabled: formModel.emailNotificationEnabled,
      twoFactorEnabled: formModel.twoFactorEnabled,
      changePassword: null,
    };

    if (formModel.newPassword) {
      model.changePassword = {
        oldPassword: formModel.oldPassword,
        newPassword: formModel.newPassword,
        confirmPassword: formModel.confirmPassword,
      };
    }

    return model;
  }

  goBack(): void {
    this.location.back();
  }

  createForm(): void {
    this.form = this.fb.group(
      {
        firstName: [''],
        lastName: [''],
        phoneNumber: ['', [phoneNumberValidator]],
        email: ['', [Validators.email]],
        oldPassword: [''],
        newPassword: ['', passwordValidator],
        confirmPassword: ['', [matchBy('newPassword')]],
        useManualReportsFiltering: [false],
        emailNotificationEnabled: [true],
        twoFactorEnabled: [false],
        twoFactorProvider: ['authenticator'],
        twoFactorSourceNumber: ['', [phoneNumberValidator]],
        twoFactorVerifyToken: ['', []],
      },
      { validators: matchFields('newPassword', 'confirmPassword') },
    );
  }

  updateColorScheme() {
    this.headerService.changeColorSchema(this.colorScheme);
  }

  twoFactorStateChanged() {
    const value = this.form.controls['twoFactorEnabled'].getRawValue();
    if (value && this.profileModel.twoFactorActivated) {
      this.resetTwoFactor();
    } else if (value) {
      this.showTwoFactorSetupDialog();
    }
  }

  resetTwoFactor() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      data: {
        title: 'Reset Two Factor Authentication',
        text: 'Are you sure you want to reset two factor authentication?',
        actionTitle: 'Reset',
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        this.showTwoFactorSetupDialog();
      }
    });
  }

  showTwoFactorSetupDialog() {
    const setupDialog = this.dialog.open(TwoFactorSetupDialogComponent, {
      width: '500px',
      height: '500px',
      disableClose: true,
    });
    setupDialog.afterClosed().subscribe(async result => {
      this.profileModel = await this.accountService.getUserProfile();
      this.ngOnChanges();
    });
  }
}
